import * as React from "react";
import {SignupInput} from "components/signup/SignupInput";
import InvalidBubble from "components/signup/shared/InvalidBubble";
import {IPasswordStrength, PasswordField} from "components/widgets/PasswordField";
import {action, observable} from "mobx";
import {UIState} from "data/UIState";
import * as EmailValidator from "email-validator";
import {observer} from "mobx-react";
import {createRef} from "react";
import {Alert} from "../../data/Alert";
import {handleSpacePressed} from "utils";


export interface IAccountData  {
  firstName:string;
  lastName:string;
  email:string;
  password:string;
  employeeId:string;
}

interface Props {
  continue:(data:IAccountData)=>void;
  pleaseWait:boolean;
}

@observer
export default class Step2Account extends React.Component<Props> {

  @observable firstName = '';
  @observable lastName = '';
  @observable email = '';
  @observable password = '';
  @observable employeeId = '';

  @observable firstNameInvalid = false;
  @observable lastNameInvalid = false;
  @observable employeeIdInvalid = false;
  @observable emailInvalid = false;
  @observable passwordStrength:IPasswordStrength|null=null;
  @observable passwordInvalid = false;

  h1Ref;

  constructor(props:Props) {
    super(props);
    this.h1Ref = createRef();
  }

  componentDidMount() {
    if(this.h1Ref && this.h1Ref.current) {
      this.h1Ref.current.focus();
    }
  }

  @action
  validateAccount = () => {



    // Make sure employee ID entered
    if(UIState.settings.EMPLOYEE_ID.ENABLED) {
      this.employeeIdInvalid = false;
      if(!this.employeeId && UIState.settings.EMPLOYEE_ID.REQUIRED) {
        this.employeeIdInvalid = true;
      } else {
        this.employeeId = this.employeeId.trim();
        if(UIState.settings.EMPLOYEE_ID.REQUIRED || this.employeeId.length>0) {
          if(UIState.settings.EMPLOYEE_ID.MAX!=-1 && this.employeeId.length>UIState.settings.EMPLOYEE_ID.MAX) {
            this.employeeIdInvalid = true;
          }
          if(this.employeeId.length<UIState.settings.EMPLOYEE_ID.MIN) {
            this.employeeIdInvalid = true;
          }
          if(UIState.settings.EMPLOYEE_ID.FORMULA=='numeric') {
            const numericPattern = /^\d+$/;
            if(!numericPattern.test(this.employeeId)) {
              this.employeeIdInvalid = true;
            }
          }
        }
      }
    }

    // Make sure user entered first name
    this.firstNameInvalid = !this.firstName;

    // Make sure user entered last name
    this.lastNameInvalid = !this.lastName;

    // test password
    if(null==this.passwordStrength || this.passwordStrength.id<2) {
      this.passwordInvalid = true;
    } else {
      this.passwordInvalid = false;
    }

    // Make sure email is valid
    this.emailInvalid = !EmailValidator.validate(this.email); // true

    const incompleteFields:string[] = [];

    if(this.firstNameInvalid) {
      incompleteFields.push('First Name');
    }
    if(this.lastNameInvalid) {
      incompleteFields.push('Last Name');
    }
    if(this.emailInvalid) {
      incompleteFields.push('Email');
    }
    if(this.employeeIdInvalid) {
      incompleteFields.push(UIState.settings.EMPLOYEE_ID.LABEL);
    }
    // only add password as incomplete if It is clear that no attempt was made to enter a password
    if(!this.password || 0===this.password.trim().length) {
      incompleteFields.push('Password');
    }
    if(incompleteFields.length==0 && !this.passwordInvalid) {
      this.props.continue( {
        firstName:this.firstName,
        lastName:this.lastName,
        email:this.email,
        password:this.password,
        employeeId:this.employeeId,
      });
    } else {
      let incompleteMessage = '';
      if(incompleteFields.length>0) {
        incompleteMessage = 'The required field';
        if (incompleteFields.length == 1) {
          incompleteMessage += ' ' + incompleteFields[0] + ' is'
        } else if (incompleteFields.length == 2) {
          incompleteMessage += 's ' + incompleteFields[0] + ' and ' + incompleteFields[1] + ' are';
        } else if (incompleteFields.length > 2) {
          incompleteMessage += 's ' + incompleteFields[0] + ', ';
          for (let i = 1; i < incompleteFields.length - 1; i++) {
            incompleteMessage += incompleteFields[i] + ', ';
          }
          incompleteMessage += 'and ' + incompleteFields[incompleteFields.length - 1] + ' are';
        }
        incompleteMessage += ' incomplete.';
      }

      // give additional password advice if it appears that the password is too weak, not just ignored
      if(this.password && 0!==this.password.trim().length) {
        if (this.passwordInvalid && this.passwordStrength && this.passwordStrength.id < 2) {
          incompleteMessage += (incompleteMessage.length>0 ? ' Additionally, y' : 'Y') + 'our password is not strong enough. It must be 8 or more characters long and contain 3 of the following: an uppercase letter, a lowercase letter, a special character, and a number.';
        }
      }

      Alert.alert(incompleteMessage + ' You must fix this before continuing.')
    }
  }


  render() {
    return (
    <>
      <div className="signup-head">
        <h1 className={'level1'} ref={this.h1Ref} tabIndex={0}>Create your account</h1>
      </div>

      <div className={'form-element'}>
        <div className="side-by-side-wrapper">
          <div className="input-side-by-side">
            <label htmlFor={"firstName"}>First Name</label>
            <SignupInput
                id="firstName"
                required invalid={this.firstNameInvalid}
                inputName="firstname"
                placeholder="first name"
                disabled={this.props.pleaseWait}
                value={this.firstName}
                updateValue={(v)=>{this.firstName=v;}}
            />
          </div>
          <div className="input-side-by-side">
            <label htmlFor={"lastName"}>Last Name</label>
            <SignupInput
                id={"lastName"}
                required
                invalid={this.lastNameInvalid}
                inputName="lastname"
                placeholder="last name"
                disabled={this.props.pleaseWait}
                value={this.lastName}
                updateValue={(v)=>{this.lastName=v;}}
            />
          </div>
        </div>
        <InvalidBubble label={'Both first and last name are required fields.'} show={this.firstNameInvalid||this.lastNameInvalid} />
      </div>



      {UIState.settings.EMPLOYEE_ID.ENABLED &&
        <div className={"form-element employee-number"}>
          <label htmlFor={"employeeid"}>{UIState.settings.EMPLOYEE_ID.LABEL}</label>
          <p className="hint right">{UIState.settings.EMPLOYEE_ID.HINT}</p>
          <div className="input-anti-pad">
            <SignupInput
                id={"employeeid"}
                required
                invalid={this.employeeIdInvalid}
                inputName="employeeid"
                placeholder={UIState.settings.EMPLOYEE_ID.PLACEHOLDER}
                disabled={this.props.pleaseWait}
                value={this.employeeId}
                updateValue={(v)=>{this.employeeId=v;}}
            />
          </div>
          <InvalidBubble label={`A valid ${UIState.settings.EMPLOYEE_ID.LABEL} is required`} show={this.employeeIdInvalid} />
        </div>
      }

      <div className={"form-element email"}>
        <label htmlFor={"email"}>Email<span className={'visuallyhidden'}> for login</span></label>
        <p className="hint right" aria-hidden={true}>Email is used for login</p>
        <div className="input-anti-pad">
          <SignupInput
              id={"email"}
              required
              invalid={this.emailInvalid}
              inputName="email"
              placeholder="enter email"
              disabled={this.props.pleaseWait}
              value={this.email}
              updateValue={(v)=>{this.email = v;}}
          />
        </div>
        <InvalidBubble label={"A valid email is required."} show={this.emailInvalid} />
      </div>

      <div className={'form-element tight password'}>
        <label htmlFor={"password"}>Password</label>
        <p className="hint">Your password must be 8 or more characters long and contain 3 of the following: an uppercase letter,
          a lowercase letter, a special character, and a number.</p>
        <div className="input-anti-pad password">
          <PasswordField
              id="password"
              required
              invalid={this.passwordInvalid}
              inputName="password"
              password={this.password}
              onPasswordChange={(p, passwordStrength)=>{
                this.password=p;
                this.passwordStrength = passwordStrength;
                this.passwordInvalid = false;
              }}
              email={this.email}
              firstName={this.firstName}
              lastName={this.lastName}
          />
        </div>
        <InvalidBubble label={"A valid password is required."} show={this.passwordInvalid} />
      </div>

      <div className={'form-element tight'}>
        {/*<ButtonAction onClick={this.validateAccount} className={"cta-button noselect"}>{this.props.pleaseWait ? "Please Wait" : "Create Account"}</ButtonAction>*/}
        <a tabIndex={0} className="cta-button noselect three-band-focus"
           onClick={this.validateAccount}
           onKeyDown={(e)=>{ handleSpacePressed(e, this.validateAccount);}}
        >{this.props.pleaseWait ? "Please Wait" : "Create Account"}</a>
      </div>

    </>
   

  );
  }
}
